import * as styles from "./Layout.module.scss";

import Footer from "@dogstrust/dt-web-core-lib-front-end/src/containers/Footer/ie";
import Header from "@dogstrust/dt-web-core-lib-front-end/src/containers/Header/ie";
import { Pagination } from "@dogstrust/dt-web-core-lib-front-end/src/containers/Pagination/Pagination";
import { useHeaderHeight } from "@dogstrust/dt-web-core-lib-front-end/src/hooks/useHeaderHeight";
import { mapPromoToSignPostCard } from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect } from "react";

interface LayoutProps {
	children: React.ReactNode;
	outsideLayoutChildren?: React.ReactNode;
	breadcrumbs: DogsTrust.Breadcrumb[];
}

export const Layout: React.FC<LayoutProps> = ({
	children,
	outsideLayoutChildren,
	breadcrumbs,
}) => {
	const {
		buildDateTime,
		header: { nodes: menuItems },
		promos: { nodes: promoItems },
		footer: { nodes: footerItems },
	} = useStaticQuery<Queries.LayoutQuery>(BUILD_QUERY);

	const sortWeight = (a: DogsTrust.MenuItem, b: DogsTrust.MenuItem) =>
		a.sort - b.sort;

	const nestedLinks: DogsTrust.MenuItem[] = menuItems
		.filter((link) => !link.drupal_parent_menu_item)
		.map((menuLink) => {
			const secondLevelItems: DogsTrust.MenuItem[] = menuItems
				.filter(
					(link) =>
						link.drupal_parent_menu_item?.replace("menu_link_content:", "") ===
						menuLink.drupal_id,
				)
				.map((secondLevelLink) => {
					const thirdLevelItems: DogsTrust.MenuItem[] = menuItems
						.filter(
							(link) =>
								link.drupal_parent_menu_item?.replace(
									"menu_link_content:",
									"",
								) === secondLevelLink.drupal_id,
						)
						.map((thirdLevelLink) => ({
							id: thirdLevelLink.drupal_id,
							title: thirdLevelLink.title,
							url: thirdLevelLink.link.uri,
							sort: thirdLevelLink.weight,
						}))
						.sort(sortWeight);

					return {
						id: secondLevelLink.drupal_id,
						title: secondLevelLink.title,
						url: secondLevelLink.link.uri,
						thirdLevelItems,
						sort: secondLevelLink.weight,
					};
				})
				.sort(sortWeight);
			return {
				id: menuLink.drupal_id,
				title: menuLink.title,
				url: menuLink.link.uri,
				secondLevelItems,
				sort: menuLink.weight,
			};
		})
		.sort(sortWeight);

	const footerLinks: DogsTrust.Link[] = footerItems
		.filter((link) => !link.drupal_parent_menu_item)
		.sort((a, b) => a.weight - b.weight)
		.map((link) => ({ title: link.title, url: link.link.uri }));

	const promoCards = promoItems.map(mapPromoToSignPostCard);

	const headerHeight = useHeaderHeight();

	useEffect(() => {
		console.log(
			`The last Gatsby Build was completed on: ${buildDateTime.datetime} || v${buildDateTime.fe_lib_version}`,
		);
		const initFavDogs = () => {
			const hasInitFavDogs = localStorage.getItem("favs-init");
			if (!hasInitFavDogs) {
				localStorage.setItem("fav-dogs", JSON.stringify([]));
				localStorage.setItem("favs-init", "true");
			}
		};
		initFavDogs();
	}, [buildDateTime]);

	return (
		<>
			<div className={styles.layoutcontainer}>
				<Header
					breadcrumbs={breadcrumbs}
					nestedLinks={nestedLinks}
					promoCards={promoCards.slice(0, 2)}
				/>
				<Pagination />

				<div className={styles.contentwrapper}>
					<main
						id="main-content"
						className={styles.inner}
						style={{
							paddingBlockStart: headerHeight,
							marginTop: 48,
						}}
						// Resolves skip to content focus issue on iOS
						tabIndex={-1}
					>
						{children}
					</main>
					<footer id="footer" className={styles.contentwrapper}>
						<div className={styles.inner}>
							<Footer footerLinks={footerLinks} />
						</div>
					</footer>
				</div>
			</div>
			{outsideLayoutChildren}
		</>
	);
};

export default Layout;

const BUILD_QUERY = graphql`
	query Layout {
		buildDateTime {
			datetime
			fe_lib_version
		}

		header: allMenuLinkContentMenuLinkContent(filter: { menu_name: { eq: "main" } }) {
			nodes {
				drupal_id
				title
				description
				link {
					uri
				}
				drupal_parent_menu_item
				weight
			}
		}
		promos: allNodePromotion(
			filter: {
				relationships: {
					field_promotion_type: { name: { eq: "PromoCardNav" } }
				}
			}
		) {
			nodes {
				...PromotionData
			}
		}

		footer: allMenuLinkContentMenuLinkContent(filter: { menu_name: { eq: "footer" } }) {
			nodes {
				title
				description
				link {
					uri
				}
				drupal_parent_menu_item
				weight
			}
		}
	}
`;
